// tslint:disable: no-any
// tslint:disable: no-unsafe-any
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthService from './auth.service';
import { IResponseObj } from 'shared/interface';
import { getUrl } from 'shared/utility';
import { environmentConfig, X_API_KEY } from 'shared/constants/constant';

const axiosInstance = axios.create();
const CancelToken = axios.CancelToken;
const cancel_req: any = {};

export { cancel_req };

export const cancelRequest = (url: string) => {
	const f = cancel_req[url];
	if (f) {
		f();
	}
};

export interface IAxiosParams extends IMiscellaneousRequestParams {
	method: string;
	url: string;
	data?: any;
	isLogin?: boolean;
	authToken?: string;
	isExpired?: boolean;
	xEnvironment?: boolean;
	xWhiteLabel?: boolean;
}

export interface IMiscellaneousRequestParams {
	xApiKey?: boolean;
	contentType?: string;
	noCache?: boolean;
}

/**
 * get method
 * @param request object containing axios params
 */
const get = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}, isExpired = false) => {
	return commonAxios({
		method: 'GET',
		url: getUrl(url, params),
		isExpired: isExpired,
		...otherData
	});
};

const getBlob = (url: string) => {
	return new Promise((resolve, reject) => {
		axios
			.get(url, { responseType: 'blob' })
			.then((response) => {
				resolve(response.data);
			})
			.catch((error) => reject(error));
	});
};

/**
 * post method
 * @param request object containing axios params
 */
const post = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) => {
	return commonAxios({
		method: 'POST',
		url: getUrl(url),
		data: params,
		...otherData
	});
};

/**
 * put method
 * @param request object containing axios params
 */
const put = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) => {
	return commonAxios({
		method: 'PUT',
		url: getUrl(url),
		data: params,
		...otherData
	});
};

/**
 * deleteRequest method
 * @param request object containing axios params
 */
const deleteRequest = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) => {
	return commonAxios({
		method: 'DELETE',
		url: getUrl(url),
		data: params,
		...otherData
	});
};

/**
 * patch method
 * @param request object containing axios params
 */
const patch = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) => {
	return commonAxios({
		method: 'PATCH',
		url: getUrl(url),
		data: params,
		...otherData
	});
};

/**
 * commonAxios
 * @param object containing method, url, data, access token, content-type, isLogin
 */
const commonAxios = ({
	method,
	url,
	data,
	contentType = 'application/json',
	noCache = false,
	xEnvironment = true,
	xWhiteLabel = true,
	xApiKey = false
}: IAxiosParams): Promise<any> => {
	const headers: any = {
		'Content-Type': contentType
	};

	const token = AuthService.getAccessToken();
	const userLang = AuthService.getUserLanguage();
	if (token) {
		headers['x-access-token'] = `${token}`;
	}

	if (noCache) {
		headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0';
	}

	if (userLang) {
		headers['x-request-language'] = userLang;
	}

	if (xEnvironment) {
		headers['x-environment'] = environmentConfig.environmentKey;
	}

	if (xWhiteLabel) {
		headers['x-whitelabel'] = environmentConfig.whiteLabelKey;
	}

	if (xApiKey) {
		headers['x-api-key'] = X_API_KEY;
	}

	let body: any = null;

	if (contentType === 'application/json') {
		body = JSON.stringify(data);
	} else {
		body = data;
	}

	return new Promise((resolve, reject) => {
		axiosInstance({
			method: method,
			url: url,
			cancelToken: new CancelToken(function executor(c) {
				// An executor function receives a cancel function as a parameter
				cancel_req[url.split('?')[0].split('v2/')[1]] = c;
			}),
			headers: headers,
			data: body
		} as AxiosRequestConfig)
			.then((response: AxiosResponse<IResponseObj<any>>) => {
				if (response && response.data) {
					resolve(response.data.data);
				}
			})
			.catch((error: Error) => {
				reject(error);
			});
	});
};

export { axiosInstance };

const HttpService = {
	get: get,
	getBlob: getBlob,
	post: post,
	put: put,
	deleteRequest: deleteRequest,
	patch: patch
};

export default HttpService;
